"use client";

import { useEffect, useRef, useState } from "react";

export const SPINNER_VARIANTS = [
  "magnify",
  "loop",
  "breathe",
  "left-to-right",
  "helix",
  "strip",
  "snake",
  "square",
  "arrow-spin",
  "wave",
  "pulse",
  "down-up",
] as const;

export type SpinnerVariant = (typeof SPINNER_VARIANTS)[number];

const SPINNER_FILE_MAP: Record<SpinnerVariant, string> = {
  magnify: "1 - Hero - Magnify.json",
  loop: "2 - The problem - Loop.json",
  breathe: "3 - Breathe.json",
  "left-to-right": "4 - Left to right.json",
  helix: "5 - Helix.json",
  strip: "6 - Strip.json",
  snake: "7 - Snake.json",
  square: "8 - Square.json",
  "arrow-spin": "9 - Arrow-Spin.json",
  wave: "10 - Wave.json",
  pulse: "11 - Pulse.json",
  "down-up": "12 - Down Up.json",
};

interface SpinnerData {
  fps: number;
  frames: number[][][];
}

interface BrailleSpinnerProps {
  variant?: SpinnerVariant;
  size?: number;
  fps?: number;
  dotScale?: number;
  color?: string;
  className?: string;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

const spinnerCache = new Map<SpinnerVariant, SpinnerData>();

export default function BrailleSpinner({
  variant = "magnify",
  size = 24,
  fps: fpsProp,
  dotScale = 0.38,
  color = "#FF5500",
  className,
}: BrailleSpinnerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [spinnerData, setSpinnerData] = useState<SpinnerData | null>(
    spinnerCache.get(variant) ?? null,
  );

  useEffect(() => {
    if (spinnerCache.has(variant)) {
      setSpinnerData(spinnerCache.get(variant)!);
      return;
    }

    const fileName = SPINNER_FILE_MAP[variant];
    fetch(`/animations/spinners/${encodeURIComponent(fileName)}`)
      .then((res) => res.json())
      .then((data) => {
        const parsed: SpinnerData = { fps: data.fps, frames: data.frames };
        spinnerCache.set(variant, parsed);
        setSpinnerData(parsed);
      })
      .catch((err) => {
        console.error(`Failed to load spinner "${variant}":`, err);
      });
  }, [variant]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !spinnerData) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const fps = fpsProp ?? spinnerData.fps;
    const frames = spinnerData.frames;
    let startTime: number | null = null;
    let rafId: number;

    const [r, g, b] = hexToRgb(color);

    function draw(ts: number) {
      if (!startTime) startTime = ts;

      const msPerFrame = 1000 / fps;
      const rawFrame =
        ((ts - startTime) % (frames.length * msPerFrame)) / msPerFrame;
      const fA = Math.floor(rawFrame) % frames.length;
      const fB = (fA + 1) % frames.length;
      const t = easeInOut(rawFrame - Math.floor(rawFrame));

      const w = canvas!.width;
      const h = canvas!.height;
      const dw = w / 8;
      const dh = h / 8;
      const rMax = Math.min(dw, dh) * dotScale;

      ctx!.clearRect(0, 0, w, h);

      const dA = frames[fA];
      const dB = frames[fB];

      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          const op = dA[row][col] + (dB[row][col] - dA[row][col]) * t;
          if (op <= 0.01) continue;
          ctx!.beginPath();
          ctx!.arc(
            (col + 0.5) * dw,
            (row + 0.5) * dh,
            rMax * op,
            0,
            Math.PI * 2,
          );
          ctx!.fillStyle = `rgba(${r},${g},${b},${op.toFixed(3)})`;
          ctx!.fill();
        }
      }

      rafId = requestAnimationFrame(draw);
    }

    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [fpsProp, dotScale, color, spinnerData]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className={className}
    />
  );
}
